class GallerySection {
  constructor(container) {
    this.container = container;
    this.galleryItems = container.querySelectorAll("[data-gallery-item]");
    this.modal = container.querySelector("[data-gallery-modal]");
    this.modalContent = container.querySelector("[data-modal-content]");
    this.closeButton = container.querySelector("[data-modal-close]");
    this.prevButton = container.querySelector("[data-modal-prev]");
    this.nextButton = container.querySelector("[data-modal-next]");
    this.backdrop = container.querySelector("[data-modal-backdrop]");

    this.currentIndex = 0;
    this.isModalOpen = false;
    this.imageCache = new Map();

    this.init();
  }

  init() {
    this.moveModalToBody();
    this.bindEvents();
    this.setupVideoElements();
  }

  moveModalToBody() {
    if (this.modal && this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
    }
  }

  bindEvents() {
    this.galleryItems.forEach((item, index) => {
      item.addEventListener("click", () => this.openModal(index));
    });

    this.closeButton?.addEventListener("click", () => this.closeModal());
    this.prevButton?.addEventListener("click", () => this.navigateModal(-1));
    this.nextButton?.addEventListener("click", () => this.navigateModal(1));
    this.backdrop?.addEventListener("click", () => this.closeModal());

    this._boundHandleKeyboard = (e) => this.handleKeyboard(e);
    // NOTE: keydown listener is registered in openModal() and removed in closeModal()
    // to avoid a live listener when the modal is not open.
  }

  setupVideoElements() {
    this.galleryItems.forEach((item) => {
      const video = item.querySelector("[data-gallery-video]");

      if (video) {
        video.addEventListener("loadedmetadata", () => {
          video.currentTime = 0;
        });
      }
    });
  }

  openModal(index) {
    this.currentIndex = index;
    this.isModalOpen = true;
    this.renderModalContent();
    this.modal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this._boundHandleKeyboard);

    setTimeout(() => {
      this.closeButton?.focus();
    }, 100);
  }

  closeModal() {
    this.isModalOpen = false;
    this.modal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this._boundHandleKeyboard);

    const video = this.modalContent.querySelector("video");
    const iframe = this.modalContent.querySelector("iframe");

    if (video) {
      video.pause();
    }

    if (iframe) {
      iframe.src = iframe.src;
    }

    this.galleryItems[this.currentIndex]?.focus();
  }

  navigateModal(direction) {
    const newIndex = this.currentIndex + direction;

    if (newIndex >= 0 && newIndex < this.galleryItems.length) {
      this.currentIndex = newIndex;
      this.renderModalContent();
    }
  }

  renderModalContent() {
    const currentItem = this.galleryItems[this.currentIndex];
    const mediaType = currentItem.dataset.mediaType;

    this.modalContent.innerHTML = "";

    if (mediaType === "video") {
      const originalVideo = currentItem.querySelector("[data-gallery-video]");
      if (originalVideo) {
        const modalVideo = originalVideo.cloneNode(true);

        modalVideo.controls = true;
        modalVideo.autoplay = true;
        modalVideo.muted = false;
        modalVideo.loop = true;
        modalVideo.removeAttribute("data-gallery-video");
        modalVideo.removeAttribute("hidden");

        this.modalContent.appendChild(modalVideo);
      }
    } else {
      const originalImage = currentItem.querySelector("[data-gallery-image]");

      if (originalImage) {
        const originalSrc =
          originalImage.dataset.originalSrc ||
          originalImage.src ||
          originalImage.dataset.src;

        if (this.imageCache.has(originalSrc)) {
          const cachedImage = this.imageCache.get(originalSrc).cloneNode(true);
          this.modalContent.appendChild(cachedImage);
        } else {
          const modalImage = document.createElement("img");
          modalImage.src = originalSrc;
          modalImage.alt = originalImage.alt || "";
          modalImage.loading = "eager";

          this.imageCache.set(originalSrc, modalImage.cloneNode(true));
          this.modalContent.appendChild(modalImage);
        }

        this.#preloadAdjacentImages();
      }
    }

    this.updateNavigationState();
  }

  #preloadAdjacentImages() {
    if (this.currentIndex < this.galleryItems.length - 1) {
      const nextItem = this.galleryItems[this.currentIndex + 1];
      const nextImage = nextItem.querySelector("[data-gallery-image]");
      if (nextImage && nextItem.dataset.mediaType !== "video") {
        const nextSrc = nextImage.dataset.originalSrc || nextImage.src;
        if (!this.imageCache.has(nextSrc)) {
          const img = new Image();
          img.src = nextSrc;
          this.imageCache.set(nextSrc, img);
        }
      }
    }

    if (this.currentIndex > 0) {
      const prevItem = this.galleryItems[this.currentIndex - 1];
      const prevImage = prevItem.querySelector("[data-gallery-image]");
      if (prevImage && prevItem.dataset.mediaType !== "video") {
        const prevSrc = prevImage.dataset.originalSrc || prevImage.src;
        if (!this.imageCache.has(prevSrc)) {
          const img = new Image();
          img.src = prevSrc;
          this.imageCache.set(prevSrc, img);
        }
      }
    }
  }

  updateNavigationState() {
    if (this.prevButton) {
      this.prevButton.style.opacity = this.currentIndex === 0 ? "0.5" : "1";
      this.prevButton.disabled = this.currentIndex === 0;
    }

    if (this.nextButton) {
      this.nextButton.style.opacity =
        this.currentIndex === this.galleryItems.length - 1 ? "0.5" : "1";
      this.nextButton.disabled =
        this.currentIndex === this.galleryItems.length - 1;
    }
  }

  handleKeyboard(event) {
    if (!this.isModalOpen) return;

    switch (event.key) {
      case "Escape":
        this.closeModal();
        break;
      case "ArrowLeft":
        this.navigateModal(-1);
        break;
      case "ArrowRight":
        this.navigateModal(1);
        break;
    }
  }

  destroy() {
    if (this.modal && this.modal.parentElement === document.body) {
      this.modal.remove();
    }
    if (this._boundHandleKeyboard) {
      // Remove in case modal was open when section was destroyed
      document.removeEventListener("keydown", this._boundHandleKeyboard);
      this._boundHandleKeyboard = null;
    }
    this.imageCache.clear();
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const gallerySections = document.querySelectorAll(
    '[data-section-type="gallery"]'
  );
  gallerySections.forEach((section) => {
    const instance = new GallerySection(section);
    section._galleryInstance = instance;
  });
});

if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:unload", (event) => {
    const section = event.target.querySelector('[data-section-type="gallery"]');
    if (section?._galleryInstance) {
      section._galleryInstance.destroy();
    }
  });
}
